/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, memo, useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {defineMessages, FormattedMessage, useIntl} from 'react-intl'
import {useLocation, Link as RouteLink} from 'react-router-dom'
import {
    Heading,
    SimpleGrid,
    Skeleton as ChakraSkeleton,
    Stack,
    Text,
    HStack,
    VStack,
    Link,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    CavendersIcon,
    CavHelpIcon,
    CowboyIcon,
    HeartIcon,
    InvertedLocatorIcon,
    OrderIcon
} from '../../../../components/icons'
import {
    carouselFullStyle,
    infoCardDescriptionStyle,
    infoCardLinkStyle,
    infoCardTitleStyle
} from './index.style'
import {pxToRem} from '../../../../utills/pxToRem'
import useSitePreference from '../../../../hooks/use-site-preference'
import RecommendedProducts from '../../../../components/recommended-products'
import {getUrlSearchParam} from '../../../../utills/url'

/**
 * This is a specialized Skeleton component that which uses the customers auth type as the
 * `isLoaded` state. It also will revert it's provided size (height, width) when the loaded
 * state changes. This allows you to have skeletons of a specific size, but once loaded
 * the bounding element will affect the contents size.
 */

const Skeleton = memo(
    forwardRef(({children, height, width, ...rest}, ref) => {
        const {data: customer} = useCurrentCustomer()
        const {isRegistered} = customer
        const size = !isRegistered ? {height, width} : {}
        return (
            <ChakraSkeleton ref={ref} isLoaded={!customer.isLoading} {...rest} {...size}>
                {children}
            </ChakraSkeleton>
        )
    })
)

Skeleton.displayName = 'Skeleton'
Skeleton.propTypes = {
    children: PropTypes.node,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isLoaded: PropTypes.bool
}

const dashboardMessages = defineMessages({
    orders: {defaultMessage: 'Orders', id: 'account_dashboard.info_card.title.orders'},
    orders_desc: {
        defaultMessage: 'View purchases, track orders, and access order history.',
        id: 'account_dashboard.info_card.desc.orders'
    },
    account_info: {
        defaultMessage: 'Profile',
        id: 'account_dashboard.info_card.title.profile'
    },
    account_info_desc: {
        defaultMessage: 'Update info, change password, and manage preferences.',
        id: 'account_dashboard.info_card.desc.account_info'
    },
    my_cavenders_desc: {
        defaultMessage: 'See available points, track point history, and view current rewards.',
        id: 'account_dashboard.info_card.desc.my_cavenders'
    },
    addresses: {defaultMessage: 'Addresses', id: 'account_dashboard.info_card.title.addresses'},
    addresses_desc: {
        defaultMessage: 'Manage shipping and billing addresses.',
        id: 'account_dashboard.info_card.desc.addresses'
    },
    wishlist: {defaultMessage: 'Wishlist', id: 'account_dashboard.info_card.title.wishlist'},
    wishlist_desc: {
        defaultMessage: 'Manage saved items, edit, remove, or move to cart.',
        id: 'account_dashboard.info_card.desc.wishlist'
    },
    need_help: {defaultMessage: 'Need Help?', id: 'account_dashboard.info_card.title.need_help'},
    need_help_desc: {
        defaultMessage: 'Have questions or need assistance? Contact us!',
        id: 'account_dashboard.info_card.desc.need_help'
    }
})

export const dashboardLinks = [
    {
        title: 'orders',
        leftIcon: {
            name: OrderIcon,
            width: pxToRem(30),
            height: pxToRem(30)
        },
        description: 'orders_desc',
        link: '/account/orders'
    },
    {
        title: 'account_info',
        leftIcon: {
            name: CowboyIcon,
            width: pxToRem(33),
            height: pxToRem(30)
        },
        description: 'account_info_desc',
        link: '/account/info'
    },
    {
        leftIcon: {
            name: CavendersIcon,
            width: pxToRem(130),
            height: 'auto',
            color: 'cav_red'
        },
        description: 'my_cavenders_desc',
        link: '/account/rewards'
    },
    {
        title: 'addresses',
        leftIcon: {
            name: InvertedLocatorIcon,
            width: pxToRem(30),
            height: pxToRem(30)
        },
        description: 'addresses_desc',
        link: '/account/addresses'
    },
    {
        title: 'wishlist',
        leftIcon: {
            name: HeartIcon,
            width: pxToRem(30),
            height: pxToRem(30)
        },
        description: 'wishlist_desc',
        link: '/account/wishlist'
    },
    {
        title: 'need_help',
        leftIcon: {
            name: CavHelpIcon,
            width: pxToRem(30),
            height: pxToRem(30)
        },
        description: 'need_help_desc',
        link: '/help'
    }
]

const InfoCard = memo(({title = null, leftIcon, description, link}) => {
    const {formatMessage} = useIntl()
    const {name: LinkIcon} = leftIcon
    return (
        <Skeleton
            display="flex"
            flexDir="column"
            layerStyle="cardBordered"
            p={{base: '15px', md: '20px 17px'}}
            gap={{base: '10px', md: '23px'}}
            boxShadow="2px 4px 16.6px 2px rgba(0, 0, 0, 0.25)"
        >
            <HStack gap={5}>
                <LinkIcon {...leftIcon} />
                {title && (
                    <Text sx={infoCardTitleStyle}>{formatMessage(dashboardMessages[title])}</Text>
                )}
            </HStack>
            <VStack align="start">
                <Text sx={infoCardDescriptionStyle}>
                    {formatMessage(dashboardMessages[description])}
                </Text>
                <Link as={RouteLink} to={link} sx={infoCardLinkStyle}>
                    <FormattedMessage
                        defaultMessage="View More"
                        id="account_dashboard.info_card.view_more"
                    />
                </Link>
            </VStack>
        </Skeleton>
    )
})

InfoCard.displayName = 'InfoCard'
InfoCard.propTypes = {
    title: PropTypes.string,
    leftIcon: PropTypes.string,
    description: PropTypes.string,
    link: PropTypes.string
}

const AccountDashboard = () => {
    const location = useLocation()
    const previousSearchParams = useMemo(() => {
        const {snitizeUrlSearchParam} = getUrlSearchParam(location.search)
        return snitizeUrlSearchParam
    }, [location.search])
    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Account Dashboard' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const {data} = useSitePreference('suggestedForYou')
    const suggestedRecommendation = data?.suggestedForYou

    return (
        <Stack
            data-testid="account-dashboard-page"
            spacing={6}
            mt={{base: '0 !important', lg: `${pxToRem(64)} !important`}}
            maxWidth={{base: '100vw'}}
        >
            <Heading
                as="h1"
                fontFamily="Montserrat"
                fontWeight="semibold"
                fontSize="22px"
                letterSpacing="3px"
                lineHeight="normal"
                tabIndex="0"
                display={{base: 'none', lg: 'block'}}
                padding={{base: '0 11px', lg: '0 45px 0 0'}}
                mb={pxToRem(25)}
                ref={headingRef}
            >
                <FormattedMessage
                    defaultMessage="Account Dashboard"
                    id="account_dashboard.title.account_dashboard"
                />
            </Heading>

            <SimpleGrid
                mt={{base: '0 !important'}}
                spacing={{base: pxToRem(15), lg: pxToRem(25)}}
                columns={[1, 1, 1, 2, 3]}
                mb={{base: '60px !important', lg: '80px !important'}}
                padding={{base: '0 11px', lg: '0 45px 0 0'}}
            >
                {dashboardLinks.map((info) => (
                    <InfoCard {...info} key={info.title} />
                ))}
            </SimpleGrid>
            <Box sx={carouselFullStyle}>
                <RecommendedProducts
                    title={suggestedRecommendation}
                    recommender={'best-sellers-null-results'}
                    previousSearchParams={previousSearchParams}
                    showQuickView={true}
                />
            </Box>
        </Stack>
    )
}

AccountDashboard.getTemplateName = () => 'account-dashboard'

export default AccountDashboard
